import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  MenuItem,
  Typography,
  Divider,
  Box,
  Chip,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { Icon } from "@iconify/react";

import ErrorAlert from "../alerts/ErrorAlert";
import Loading from "../spinners/Loading";

import {
  useGetEventsQuery,
  useCreateEventMutation,
  useAddAttendeesMutation,
} from "../../features/events/eventsApiSlice";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";

function splitMemberIds(members = []) {
  return members
    .map((m) => {
      if (!m) return null;
      if (typeof m === "string") return m;
      if (typeof m === "number") return String(m);
      return m?._id || m?.id || m?.memberId || null;
    })
    .filter(Boolean);
}

const STATUS_COLORS = {
  geplant: "info",
  abgeschlossen: "success",
  abgesagt: "error",
};

export default function EventSelectDialog({
  open,
  onClose,
  selectedMembers = [],
  defaultTitle = "Zu Veranstaltung hinzufügen",
}) {
  const dispatch = useDispatch();

  const { data: events = [], isLoading, isError } = useGetEventsQuery();

  const [createEvent, { isLoading: isCreating, error: createError }] =
    useCreateEventMutation();
  const [addAttendees, { isLoading: isAdding, error: addError }] =
    useAddAttendeesMutation();

  const regularEvents = useMemo(
    () =>
      Array.isArray(events)
        ? events.filter(
            (e) => e?.eventType !== "TESTING" && e?.status !== "abgesagt"
          )
        : [],
    [events]
  );

  const memberIds = useMemo(
    () => splitMemberIds(selectedMembers),
    [selectedMembers]
  );

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      modeCreate: regularEvents.length === 0,
      existingEventId: "",
      eventName: "",
      eventDateBegin: "",
      eventDateEnd: "",
      locationName: "",
      city: "",
      zip: "",
      street: "",
      description: "",
    },
  });

  const modeCreate = watch("modeCreate");
  const existingEventId = watch("existingEventId");

  useEffect(() => {
    if (!open) return;
    reset((prev) => ({
      ...prev,
      modeCreate: regularEvents.length === 0,
      existingEventId:
        regularEvents.length > 0
          ? regularEvents[0]._id || regularEvents[0].id
          : "",
    }));
  }, [open, regularEvents, reset]);

  const selectedEvent = useMemo(
    () => regularEvents.find((e) => (e._id || e.id) === existingEventId),
    [regularEvents, existingEventId]
  );

  const onSubmit = async (data) => {
    if (!memberIds.length) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Keine Mitglieder ausgewählt",
          data: { errorText: "Bitte wählen Sie mindestens ein Mitglied aus." },
        })
      );
      return;
    }

    try {
      let targetEventId = data.existingEventId;

      if (data.modeCreate) {
        const payload = {
          eventName: data.eventName?.trim(),
          eventType: "EVENT",
          locationName: data.locationName?.trim() || undefined,
          eventDateBegin: data.eventDateBegin || undefined,
          eventDateEnd: data.eventDateEnd || undefined,
          city: data.city?.trim() || undefined,
          zip: data.zip?.trim() || undefined,
          street: data.street?.trim() || undefined,
          description: data.description?.trim() || undefined,
        };

        if (!payload.eventName) return;

        const res = await createEvent(payload).unwrap();
        targetEventId = res?.event?._id || res?.event?.id;
      } else {
        if (!targetEventId) return;
      }

      await addAttendees({ id: targetEventId, memberIds }).unwrap();

      dispatch(
        setDialog({
          title: "Mitglieder zur Veranstaltung hinzugefügt",
          type: "SUCCESS",
          data: {
            successText: `${memberIds.length} Mitglied(er) wurden zur Veranstaltung hinzugefügt.`,
          },
        })
      );
    } catch (e) {
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText:
              e?.data?.message ||
              "Es ist ein Fehler beim Hinzufügen der Teilnehmer aufgetreten.",
          },
        })
      );
    } finally {
      onClose?.();
    }
  };

  const busy = isLoading || isCreating || isAdding;

  return (
    <Dialog open={open} onClose={() => onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Icon icon="mdi:calendar-plus" width={24} />
        {defaultTitle}
      </DialogTitle>
      <DialogContent dividers>
        {busy && (
          <Box sx={{ my: 1 }}>
            <Loading />
          </Box>
        )}

        {isError && (
          <Box sx={{ mb: 1 }}>
            <ErrorAlert errorMessage="Fehler beim Laden der Veranstaltungen." />
          </Box>
        )}

        {createError && (
          <Box sx={{ mb: 1 }}>
            <ErrorAlert errorMessage="Fehler beim Erstellen der Veranstaltung." />
          </Box>
        )}

        {addError && (
          <Box sx={{ mb: 1 }}>
            <ErrorAlert errorMessage="Fehler beim Hinzufügen der Teilnehmer." />
          </Box>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="form">
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                background: "rgba(124,58,237,0.06)",
                border: "1px solid rgba(124,58,237,0.15)",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon icon="mdi:account-group" width={20} color="#7c3aed" />
              <Typography variant="body2">
                <strong>{memberIds.length}</strong> Mitglied(er) ausgewählt
              </Typography>
            </Box>

            <Controller
              name="modeCreate"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label={
                    field.value
                      ? "Neue Veranstaltung erstellen"
                      : "Bestehende Veranstaltung wählen"
                  }
                />
              )}
            />

            {!modeCreate && (
              <>
                <Controller
                  name="existingEventId"
                  control={control}
                  rules={{ required: "Bitte eine Veranstaltung auswählen." }}
                  render={({ field }) => (
                    <TextField
                      select
                      label="Veranstaltung auswählen"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.existingEventId}
                      helperText={
                        errors.existingEventId?.message ||
                        `${regularEvents.length} Veranstaltung(en) verfügbar.`
                      }
                    >
                      {regularEvents.map((e) => (
                        <MenuItem key={e._id || e.id} value={e._id || e.id}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <span>{e.eventName}</span>
                            <Chip
                              size="small"
                              label={e.status || "geplant"}
                              color={STATUS_COLORS[e.status] || "default"}
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          </Stack>
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />

                {selectedEvent && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: "action.hover",
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      {selectedEvent.eventName}
                    </Typography>
                    {selectedEvent.eventDateBegin && (
                      <Typography variant="caption" display="block">
                        Datum:{" "}
                        {new Date(
                          selectedEvent.eventDateBegin
                        ).toLocaleDateString("de-DE")}
                      </Typography>
                    )}
                    {selectedEvent.locationName && (
                      <Typography variant="caption" display="block">
                        Ort: {selectedEvent.locationName}
                      </Typography>
                    )}
                    {selectedEvent.hasAttendees?.length > 0 && (
                      <Typography variant="caption" display="block">
                        Bereits eingetragen:{" "}
                        {selectedEvent.hasAttendees.length} Teilnehmer
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}

            {modeCreate && (
              <>
                <Controller
                  name="eventName"
                  control={control}
                  rules={{
                    required:
                      "Bitte einen Namen für die Veranstaltung angeben.",
                  }}
                  render={({ field }) => (
                    <TextField
                      label="Name der Veranstaltung"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.eventName}
                      helperText={errors.eventName?.message}
                      required
                    />
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Beschreibung (optional)"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      minRows={2}
                    />
                  )}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Controller
                    name="eventDateBegin"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Beginn"
                        type="datetime-local"
                        value={field.value}
                        onChange={field.onChange}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="eventDateEnd"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Ende (optional)"
                        type="datetime-local"
                        value={field.value}
                        onChange={field.onChange}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    )}
                  />
                </Stack>

                <Divider />

                <Controller
                  name="locationName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Ort / Halle"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Stadt"
                        value={field.value}
                        onChange={field.onChange}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="zip"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="PLZ"
                        value={field.value}
                        onChange={field.onChange}
                        fullWidth
                      />
                    )}
                  />
                </Stack>

                <Controller
                  name="street"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Straße"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </>
            )}
          </Stack>

          <DialogActions sx={{ px: 0, mt: 2 }}>
            <Button onClick={() => onClose?.()} color="inherit">
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Icon icon="mdi:calendar-plus" />}
              disabled={
                busy ||
                (!modeCreate && !existingEventId) ||
                (modeCreate && !!errors.eventName)
              }
              fullWidth
              sx={{
                fontSize: "110%",
                background:
                  "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)",
                },
              }}
            >
              Zur Veranstaltung hinzufügen
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}

EventSelectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  selectedMembers: PropTypes.array,
  defaultTitle: PropTypes.string,
};
